<template>
  <div class="flex flex-col">
    <div class="flex flex-col items-center justify-center gap-2 self-stretch">
      <div class="flex w-full flex-wrap items-center gap-3" :class="alignmentClass">
        <div class="flex flex-col flex-wrap gap-2">
          <div class="flex flex-wrap gap-2">
            <div v-for="index in element.count" :key="index">
              <component
                :is="iconComponent"
                class="rating-icon h-8 w-8"
                :style="{
                  strokeWidth: '1',
                  fill: element.inactiveColor ? '#' + element.inactiveColor : '#E5E7EB',
                  stroke: 'none',
                }"
              />
            </div>
          </div>

          <div class="flex w-auto justify-between text-sm text-gray-600">
            <template v-if="isActive">
              <input
                v-model="lowestRating"
                :placeholder="t('common.rating.lowestRating')"
                class="rating-input short-label inline-block w-20 border-0 p-0 text-sm text-gray-600 focus:outline-none"
                @input="updateLowestRating"
              />
            </template>
            <template v-else>
              <span class="rating-text short-label inline-block w-20">{{
                element.lowestRating
              }}</span>
            </template>

            <template v-if="isActive">
              <input
                v-model="highestRating"
                :placeholder="t('common.rating.highestRating')"
                class="rating-input short-label inline-block w-20 border-0 p-0 text-right text-sm text-gray-600 focus:outline-none"
                @input="updateHighestRating"
              />
            </template>
            <template v-else>
              <span class="rating-text short-label text-right">{{ element.highestRating }}</span>
            </template>
          </div>
        </div>
      </div>
    </div>
    <template v-if="isActive">
      <input
        v-model="shortLabel"
        :placeholder="t('common.shortLabelPlaceholder')"
        class="short-label border-0 p-0 focus:outline-none"
        @input="updateShortLabel"
      />
    </template>
    <template v-else>
      <span v-if="element.shortLabel" class="short-label">
        {{ element.shortLabel }}
      </span>
    </template>
  </div>
</template>

<script setup lang="ts">
// 1. Imports
import { RATING_ELEMENTS } from "@/util/constants";
import { computed, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

// 2. Type Definitions
interface RatingElement {
  iconAlignment: "left" | "right" | "center";
  count: number;
  iconType: string;
  color?: string;
  strokeColor?: string;
  lowestRating: string;
  highestRating: string;
  shortLabel?: string;
  inactiveColor?: string;
}

interface RatingElementProps {
  element: RatingElement;
  type?: string;
  placeholder?: string;
  classList?: string[];
  styleObject?: Record<string, string>;
  readonly?: boolean;
  isActive?: boolean;
}

// 3. Props Declaration with Default Values
const props = withDefaults(defineProps<RatingElementProps>(), {
  type: "input",
  placeholder: "",
  classList: () => [],
  styleObject: () => ({
    fontSize: "14px",
  }),
  readonly: false,
  isActive: false,
});

// 4. Emits Declaration
const emit = defineEmits<{
  "update:placeholder": [value: string];
  "update:shortLabel": [value: string];
  "update:lowestRating": [value: string];
  "update:highestRating": [value: string];
}>();

const shortLabel = ref(props.element.shortLabel || "");
const lowestRating = ref(props.element.lowestRating || "");
const highestRating = ref(props.element.highestRating || "");

// 5. Computed Properties
const iconComponent = computed(
  () => RATING_ELEMENTS.find((icon) => icon.name === props.element.iconType)?.icon
);

const alignmentClass = computed(() => ({
  "justify-start": props.element.iconAlignment === "left",
  "justify-end": props.element.iconAlignment === "right",
  "justify-center": props.element.iconAlignment === "center" || !props.element.iconAlignment,
  "self-stretch": true,
}));

// 6. Watchers
watch(
  () => props.element.shortLabel,
  (newVal) => {
    shortLabel.value = newVal || "";
  }
);

watch(
  () => props.element.lowestRating,
  (newVal) => {
    lowestRating.value = newVal || "";
  }
);

watch(
  () => props.element.highestRating,
  (newVal) => {
    highestRating.value = newVal || "";
  }
);

// 7. Methods
const updateShortLabel = (event?: InputEvent) => {
  if (event?.target instanceof HTMLInputElement) {
    shortLabel.value = event.target.value;
    emit("update:shortLabel", shortLabel.value);
  }
};

const updateLowestRating = (event?: InputEvent) => {
  if (event?.target instanceof HTMLInputElement) {
    lowestRating.value = event.target.value;
    emit("update:lowestRating", lowestRating.value);
  }
};

const updateHighestRating = (event?: InputEvent) => {
  if (event?.target instanceof HTMLInputElement) {
    highestRating.value = event.target.value;
    emit("update:highestRating", highestRating.value);
  }
};

// 8. Lifecycle Hooks
onUnmounted(() => {
  shortLabel.value = "";
});
</script>

<style>
.rating-input {
  background-color: transparent;
  margin-top: 4px;
  font-size: 14px;
  color: #4b5563;
  min-width: 0;
  max-width: 100%;
}

.rating-text {
  word-break: break-word;
  hyphens: auto;
  overflow-wrap: break-word;
  min-width: 0;
  max-width: 100%;
}
</style>
